<script setup lang="ts">
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import { getUserStatistics, type UserStatistics } from '@/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import {
  buildStatisticsDateQuery,
  formatStatisticsNumber,
  type StatisticsFiltersProps,
} from '@/utils/adminStatistics';

dayjs.extend(relativeTime);

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<UserStatistics | undefined>(undefined);
const loading = ref(false);

const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    {
      label: 'Active users',
      value: formatStatisticsNumber(data.value.active_user_count),
      suffix: `of ${formatStatisticsNumber(data.value.total_user_count)}`,
    },
  ];
});

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getUserStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });
    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load user statistics');
    logger.error('Failed to load user statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <n-flex v-if="data !== undefined" vertical :size="16">
      <statistics-kpi-grid :tiles="kpiTiles" />

      <n-grid cols="1 l:2" responsive="screen" :x-gap="16" :y-gap="16">
        <n-gi>
          <n-card title="Latest usage" size="small">
            <n-table size="small" :single-line="false">
              <thead>
                <tr>
                  <th>Full name</th>
                  <th>Email</th>
                  <th>Organisation</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in data.recent_log_ins" :key="user.uid">
                  <td>{{ user.full_name }}</td>
                  <td>{{ user.email }}</td>
                  <td>{{ user.organisation_name }}</td>
                  <td>
                    <n-text depth="3">{{ dayjs(user.last_logged_in).fromNow() }}</n-text>
                  </td>
                </tr>
              </tbody>
            </n-table>
          </n-card>
        </n-gi>
        <n-gi>
          <n-card title="Newest users" size="small">
            <n-table size="small" :single-line="false">
              <thead>
                <tr>
                  <th>Full name</th>
                  <th>Email</th>
                  <th>Organisation</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in data.newest_users" :key="user.uid">
                  <td>{{ user.full_name }}</td>
                  <td>{{ user.email }}</td>
                  <td>{{ user.organisation_name }}</td>
                  <td>
                    <n-text depth="3">{{ dayjs(user.created_at).fromNow() }}</n-text>
                  </td>
                </tr>
              </tbody>
            </n-table>
          </n-card>
        </n-gi>
      </n-grid>
    </n-flex>
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
